import { DataBooking, DataEvent, ExtendedDataEvent, ValueType } from '@components/list-card/src/list-card.type';
import { DATE_FORMAT_MONTH_WITH_YEAR, DATE_FORMAT_SHORT_DATE_SHORT_YEAR } from '@constants/datetime';
import { DEFAULT_CURRENCY } from '@constants/global';
import { CompanyStatsResponse } from '@hooks/company/use-get-company-stats';
import { DateRangePickerItemProps } from '@tremor/react';
import { createDayNumber, createMonthValue, createWeekDay, createYearValue } from '@utils/format-date/format-date';
import { formatNumber, formatValue } from '@utils/format-number/format-number';
import { TFunction } from 'i18next';
import moment from 'moment';

export const chartTabs = [
  {
    id: 'day',
    name: 'Day',
  },
  {
    id: 'week',
    name: 'Week',
  },
  {
    id: 'month',
    name: 'Month',
  },
];

export const currencyValueFormatter = (number: number, locale: string, currency?: string, shortened?: boolean) => {
  return formatNumber({
    locale,
    currency: currency ?? DEFAULT_CURRENCY,
    number,
    minimumFractionDigits: 0,
    shortened,
  });
};

export const numberValueFormatter = (number: number, locale: string) => {
  return formatValue({
    locale,
    number,
    shortened: true,
  });
};

const getNumber = (val: number | undefined): number => (typeof val === 'number' ? val : 0);
const getTotal = (eventValue: DataEvent, value: ValueType) =>
  getNumber(eventValue?.[`offline_${value}`]?.total as number) +
  getNumber(eventValue?.[`online_${value}`]?.total as number);

const sortedEvents = (eventList: DataEvent[] | undefined, value: ValueType, isOfflineToggleActive: boolean) => {
  if (!eventList) return [];
  const totalEvents = eventList
    .sort((eventA, eventB) => {
      if (isOfflineToggleActive) {
        const totalB = getTotal(eventB, value);
        const totalA = getTotal(eventA, value);
        return totalB - totalA;
      }
      return eventB?.[`online_${value}`]?.total - eventA?.[`online_${value}`]?.total;
    })
    .filter((_, index) => index < 30)
    .sort((eventA, eventB) => eventA?.starting_time - eventB?.starting_time);
  return totalEvents;
};

const formatEventDate = (starting_time: number, dateType?: 'day' | 'week' | 'month'): string => {
  const date = moment(starting_time, 'X');
  if (dateType === 'week') {
    const startWeekDay = date.clone().startOf('week');
    const endWeekDay = date.clone().endOf('week');
    return `${startWeekDay.format(DATE_FORMAT_SHORT_DATE_SHORT_YEAR)} - ${endWeekDay.format(DATE_FORMAT_SHORT_DATE_SHORT_YEAR)}`;
  }
  if (dateType === 'month') return date.format(DATE_FORMAT_MONTH_WITH_YEAR);

  return date.format(DATE_FORMAT_SHORT_DATE_SHORT_YEAR);
};

export const barData = ({
  dateType,
  eventList,
  value,
  showOffline,
  translations,
}: {
  dateType?: 'day' | 'week' | 'month';
  eventList: DataEvent[];
  value: ValueType;
  showOffline: boolean;
  translations: TFunction<'translation', undefined>;
}) =>
  sortedEvents(eventList, value, showOffline).map((event) => {
    const onlineOfferValue = event?.[`online_${value}`].offers || {};
    const offlineOfferValue = event?.[`offline_${value}`].offers || {};
    const onlineAddOnsValue = event?.online_revenue?.add_ons;
    const formattedDate = formatEventDate(event.starting_time, dateType);
    const ticketColumnName = translations('offers:admissionTicket');
    const bottleServiceColumnName = translations('offers:admissionBottleServiceTitle');
    const passColumnName = translations('passes:seasonLabelType');
    const guestListColumnName = translations('offers:admissionGuestListTitle');
    const addonsColumnName = translations('addons:addOnSectionTitle');
    let data;
    const isEventTab = !dateType; // if is booking tab we have date type
    const isRevenueInEventTab = value === 'revenue' && isEventTab;
    if (showOffline) {
      data = {
        starting_time: formattedDate,
        name: `${event.name} (${formattedDate})`,
        [ticketColumnName]:
          getNumber(onlineOfferValue.ticket as number) + getNumber(offlineOfferValue.ticket as number),
        [passColumnName]: getNumber(onlineOfferValue.pass as number) + getNumber(offlineOfferValue.pass as number),
        [bottleServiceColumnName]:
          getNumber(onlineOfferValue.bottle_service as number) + getNumber(offlineOfferValue.bottle_service as number),
        [guestListColumnName]:
          getNumber(onlineOfferValue.guest_list as number) + getNumber(offlineOfferValue.guest_list as number),
      };
      if (isRevenueInEventTab) {
        data = {
          ...data,
          [addonsColumnName]: getNumber((onlineAddOnsValue?.total as number) ?? 0),
        };
      }
      return data;
    }

    data = {
      starting_time: formattedDate,
      name: `${event.name} (${formattedDate})`,
      [ticketColumnName]: getNumber(onlineOfferValue.ticket as number),
      [passColumnName]: getNumber(onlineOfferValue.pass as number),
      [bottleServiceColumnName]: getNumber(onlineOfferValue.bottle_service as number),
      [guestListColumnName]: getNumber(onlineOfferValue.guest_list as number),
    };

    if (isRevenueInEventTab) {
      data = {
        ...data,
        [addonsColumnName]: getNumber((onlineAddOnsValue?.total as number) ?? 0),
      };
    }
    return data;
  });

const formatAnalyiticsDate = (date: number, timezone: string) => {
  return {
    starting_date: moment(date * 1000)
      .tz(timezone)
      .toLocaleString(),
    year: createYearValue(date, timezone),
    weekday: createWeekDay(date, timezone),
    month: createMonthValue(date, timezone),
    dayNumber: createDayNumber(date, timezone),
  };
};

export const extendData = (
  analyticsData: DataEvent[] | DataBooking[] | undefined,
  timezone: string,
  locale: string
): ExtendedDataEvent[] => {
  return (analyticsData ?? []).map((item) => {
    const commonProperties = {
      total_revenue_formatted: formatNumber({
        locale,
        currency: item.currency,
        number: item.online_revenue.total + item.offline_revenue.total,
      }),
      total_sold_items_formatted: formatValue({
        locale,
        number: item.online_sold_items.total + item.offline_sold_items.total,
      }),
      online_revenue: {
        ...item.online_revenue,
        total_formatted: formatNumber({
          locale,
          currency: item.currency,
          number: item.online_revenue.total as number,
        }),
      },
      offline_revenue: {
        ...item.offline_revenue,
        total_formatted: formatNumber({ locale, currency: item.currency, number: item.offline_revenue.total }),
      },
      online_sold_items: {
        ...item.online_sold_items,
        total_formatted: formatValue({ locale, number: item.online_sold_items.total }),
      },
      offline_sold_items: {
        ...item.offline_sold_items,
        total_formatted: formatValue({ locale, number: item.offline_sold_items.total }),
      },
    };
    const date = 'starting_time' in item ? (item as DataEvent).starting_time : (item as DataBooking).date;
    const commonItemProperties = {
      ...item,
      ...commonProperties,
      ...formatAnalyiticsDate(date, timezone),
    };
    return {
      ...commonItemProperties,
      starting_time: date,
    };
  });
};

export const extendStats = (stats: CompanyStatsResponse['stats'] | undefined) => {
  if (!stats) return;
  return {
    ...stats,
    total_revenue: stats.online_revenue.total + stats.offline_revenue.total,
    total_sold_items: stats.online_sold_items.total + stats.offline_sold_items.total,
  };
};
const today = (locale: string) => moment().locale(locale).toDate();
export interface ExtendedDateRangePickerItemProps extends DateRangePickerItemProps {
  label: string;
}
export const bookingFilterDates = (locale: string, t: TFunction): ExtendedDateRangePickerItemProps[] => [
  //Last Week: last week 1st to last day
  {
    from: moment().locale(locale).subtract(7, 'days').startOf('isoWeek').toDate(),
    to: moment().locale(locale).subtract(1, 'week').endOf('isoWeek').toDate(),
    value: 'LW',
    label: t('analytics:datePickerLabelLastWeek'),
  },
  //Last 7 days: today - 7 days
  {
    from: moment().locale(locale).subtract(7, 'days').toDate(),
    to: today(locale),
    value: 'L7D',
    label: t('analytics:datePickerLabelLast7Days'),
  },
  //Last Month: last month 1st to last day
  {
    from: moment().locale(locale).subtract(1, 'month').startOf('month').toDate(),
    to: moment().locale(locale).subtract(1, 'month').endOf('month').toDate(),
    value: 'LM',
    label: t('analytics:datePickerLabelLastMonth'),
  },
  //Last 30 days --> default: today - 30 days
  {
    from: moment().locale(locale).subtract(30, 'days').toDate(),
    to: today(locale),
    value: 'L30D',
    label: t('analytics:datePickerLabelLast30Days'),
  },
  //Month to date: last month 1st to today
  {
    from: moment().locale(locale).subtract(1, 'month').startOf('month').toDate(),
    to: today(locale),
    value: 'MTD',
    label: t('analytics:datePickerLabelMonthToDate'),
  },
  //Last Year: today - 365 days
  {
    from: moment().locale(locale).subtract(365, 'days').toDate(),
    to: today(locale),
    value: 'LY',
    label: t('analytics:datePickerLabelLastYear'),
  },
  //Year to date: 1 Jan current year to today.
  {
    from: moment().locale(locale).startOf('year').toDate(),
    to: today(locale),
    value: 'CY',
    label: t('analytics:datePickerLabelCurrentYear'),
  },
];
export const eventFilterDates = (locale: string, t: TFunction): ExtendedDateRangePickerItemProps[] => [
  // Upcoming (= today + 365 days) --> default
  {
    from: today(locale),
    to: moment().locale(locale).add(365, 'days').toDate(),
    value: 'UY',
    label: t('analytics:datePickerLabelUpcomingYear'),
  },
  // This week (from this week monday to today)
  {
    from: moment().locale(locale).startOf('isoWeek').toDate(),
    to: moment().locale(locale).endOf('isoWeek').toDate(),
    value: 'CW',
    label: t('analytics:datePickerLabelCurrentWeek'),
  },
  // This month (from day 1 to today)
  {
    from: moment().locale(locale).startOf('month').toDate(),
    to: moment().locale(locale).endOf('month').toDate(),
    value: 'CM',
    label: t('analytics:datePickerLabelCurrentMonth'),
  },
  // Last month (from day 1 to last day of last month)
  {
    from: moment().locale(locale).subtract(1, 'month').startOf('month').toDate(),
    to: moment().locale(locale).subtract(1, 'month').endOf('month').toDate(),
    value: 'LM',
    label: t('analytics:datePickerLabelLastMonth'),
  },
  // This Year (from day Jan 1st current year to today)
  {
    from: moment().locale(locale).startOf('year').toDate(),
    to: moment().locale(locale).endOf('year').toDate(),
    value: 'CY',
    label: t('analytics:datePickerLabelCurrentYear'),
  },
  // Last Year (from day Jan 1st current year to end of last year)
  {
    from: moment().locale(locale).subtract(1, 'year').startOf('year').toDate(),
    to: moment().locale(locale).subtract(1, 'year').endOf('year').toDate(),
    value: 'LY',
    label: t('analytics:datePickerLabelPastYear'),
  },
];
